import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import useStore from '../store'
import { logger } from '../utils/logger'

const { token } = useStore.getState().userCredentials
const BASE_URL = import.meta.env.VITE_BASE_URL ?? 'http://localhost:3080'
const VITE_ADMIN_URL = import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:8000'

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : ''
  }
})

export const laravelApi = axios.create({
  baseURL: VITE_ADMIN_URL,
  headers: {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : ''
  }
})

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // No registrar requests a New Relic
    if (config.url?.includes('js-agent.newrelic.com') || config.baseURL?.includes('newrelic.com')) {
      return config
    }

    const method = config.method?.toUpperCase() || 'GET'
    const url = config.url || ''
    const fullUrl = `${config.baseURL}${url}`

    try {
      logger.logApiRequest(method, fullUrl, config.data)
    } catch {
      // Ignorar errores de logging
    }

    return config
  },
  (error: AxiosError) => {
    // No registrar errores de New Relic
    const errorMessage = error.message || ''
    if (
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Loading chunk') ||
      error.config?.url?.includes('js-agent.newrelic.com')
    ) {
      return Promise.reject(error)
    }

    try {
      logger.logError(error, {
        type: 'request_interceptor_error',
        url: error.config?.url
      })
    } catch {
      // Ignorar errores de logging
    }
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful API calls if needed
    if (import.meta.env.MODE !== 'development') {
      try {
        logger.addPageAction('api_success', {
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          status: response.status
        })
      } catch {
        // Ignorar errores de logging
      }
    }
    return response
  },
  (error: AxiosError) => {
    // No registrar errores de New Relic
    const errorMessage = error.message || ''
    if (
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('nr-spa') ||
      error.config?.url?.includes('js-agent.newrelic.com')
    ) {
      return Promise.reject(error)
    }

    const method = error.config?.method?.toUpperCase() || 'GET'
    const url = error.config?.url || ''
    const status = error.response?.status || 0
    const errorData = error.response?.data

    try {
      logger.logApiError(method, url, status, errorData)
    } catch {
      // Ignorar errores de logging
    }

    return Promise.reject(error)
  }
)

// Request interceptor for laravelApi
laravelApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // No registrar requests a New Relic
    if (config.url?.includes('js-agent.newrelic.com') || config.baseURL?.includes('newrelic.com')) {
      return config
    }

    const method = config.method?.toUpperCase() || 'GET'
    const url = config.url || ''
    const fullUrl = `${config.baseURL}${url}`

    try {
      logger.logApiRequest(method, fullUrl, config.data)
    } catch {
      // Ignorar errores de logging
    }

    return config
  },
  (error: AxiosError) => {
    // No registrar errores de New Relic
    const errorMessage = error.message || ''
    if (
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Loading chunk') ||
      error.config?.url?.includes('js-agent.newrelic.com')
    ) {
      return Promise.reject(error)
    }

    try {
      logger.logError(error, {
        type: 'request_interceptor_error',
        url: error.config?.url,
        api: 'laravel'
      })
    } catch {
      // Ignorar errores de logging
    }
    return Promise.reject(error)
  }
)

// Response interceptor for laravelApi
laravelApi.interceptors.response.use(
  (response: AxiosResponse) => {
    if (import.meta.env.MODE !== 'development') {
      try {
        logger.addPageAction('api_success', {
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          status: response.status,
          api: 'laravel'
        })
      } catch {
        // Ignorar errores de logging
      }
    }
    return response
  },
  (error: AxiosError) => {
    // No registrar errores de New Relic
    const errorMessage = error.message || ''
    if (
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Loading chunk') ||
      error.config?.url?.includes('js-agent.newrelic.com')
    ) {
      return Promise.reject(error)
    }

    const method = error.config?.method?.toUpperCase() || 'GET'
    const url = error.config?.url || ''
    const status = error.response?.status || 0
    const errorData = error.response?.data

    try {
      logger.logApiError(method, url, status, errorData)
    } catch {
      // Ignorar errores de logging
    }

    return Promise.reject(error)
  }
)

export default api
